import styled, { css } from "styled-components";
import { Colors, HTMLTable, HTMLSelect } from "@blueprintjs/core";
import type { ColumnPinningPosition } from "@tanstack/react-table";
import { sizes } from "@/shared/styles";
import { SizeType } from "@/shared/components/ResizeSensor";

interface ITableContainerProps {
  showPagination?: boolean;
  scroll?: SizeType;
}
export const TableContainer = styled.div<ITableContainerProps>`
  position: relative;
  overflow: auto;
  ${(props) => {
    if (props.showPagination) {
      if (!!props.scroll) {
        return css`
          /* padding-bottom: ${sizes.paginationHeight + 1}px; */
          height: ${props.scroll.height - sizes.paginationHeight + 1}px;
          width: ${props.scroll.width}px;
        `;
      }
    } else {
      if (!!props.scroll) {
        return css`
          height: ${props.scroll.height}px;
          width: ${props.scroll.width}px;
        `;
      }
    }
  }}
`;
export const TableStyled = styled(HTMLTable)`
  table-layout: fixed;
  width: 100%;

  th {
    position: relative;

    td {
      vertical-align: middle !important;
    }
  }

  &.bp4-html-table-striped {
    tbody {
      tr {
        td {
          background: #ffffff;
        }
      }
      tr:nth-child(odd) {
        td {
          background: #eef0f2 !important;
        }
      }
      tr:hover {
        td {
          background: #dde0e5 !important;
        }
      }
      tr:active {
        td {
          background: #d8dbe1 !important;
        }
      }
    }
  }
`;

export const TableHeader = styled.thead`
  ${TableStyled} & {
    tr {
      th {
        position: sticky;
        top: 0; /* 第一列最上 */
        background-color: ${Colors.WHITE};
        box-shadow: inset 0 -1px 0 0 rgb(17 20 24 / 15%);
        z-index: 9;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        vertical-align: middle;
      }
    }
  }
`;

interface ITableTHProps {
  pin?: ColumnPinningPosition;
  stickyWidth?: number;
  isLeftPinLast?: boolean;
  isRightPinFirst?: boolean;
}
export const TableTH = styled.th<ITableTHProps>`
  user-select: none;
  ${(props) =>
    props.pin === "left" &&
    css`
      position: sticky !important;
      left: ${props.stickyWidth}px;
      z-index: 20 !important;
    `}

  ${(props) =>
    props.pin === "right" &&
    css`
      position: sticky !important;
      right: ${props.stickyWidth}px;
      z-index: 20 !important;
    `}

    ${(props) =>
    props.isLeftPinLast &&
    css`
      border-right: solid 1px rgba(0, 0, 0, 0.2);
    `}

    ${(props) =>
    props.isRightPinFirst &&
    css`
      border-left: solid 1px rgba(0, 0, 0, 0.2);
    `}
`;

export const HeaderContent = styled.div`
  display: flex;
  /* justify-content: space-between; */
  align-items: center;
`;

export const TableBody = styled.tbody`
  ${TableStyled} & {
    tr {
      &:first-child td {
        box-shadow: none !important;
      }

      &:last-child td {
        border-bottom: solid 1px rgba(0, 0, 0, 0.2);
      }

      td {
        vertical-align: middle;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        * {
          z-index: 2 !important;
        }
      }
    }
  }
`;

interface ITableTDProps {
  pin?: ColumnPinningPosition;
  stickyWidth?: number;
  isLeftPinLast?: boolean;
  isRightPinFirst?: boolean;
}
export const TableTD = styled.td<ITableTDProps>`
  ${(props) =>
    props.pin === "left" &&
    css`
      position: sticky;
      z-index: 10;
      left: ${props.stickyWidth}px;
    `}

  ${(props) =>
    props.pin === "right" &&
    css`
      position: sticky;
      z-index: 10;
      right: ${props.stickyWidth}px;
    `}

    ${(props) =>
    props.isLeftPinLast &&
    css`
      border-right: solid 1px rgba(0, 0, 0, 0.3);
    `}

    ${(props) =>
    props.isRightPinFirst &&
    css`
      border-left: solid 1px rgba(0, 0, 0, 0.2);
    `}
`;

export const TableFooter = styled.tfoot`
  position: sticky;
  bottom: 0;
  background: white;
  z-index: 10;
  tr {
    position: relative;
    th {
      background-color: white;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`;

interface ITableResizerProps {
  resizing?: boolean;
}
export const TableResizer = styled.div<ITableResizerProps>`
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  width: 3px;
  background: rgba(0, 0, 0, 0.5);
  cursor: col-resize;
  user-select: none;
  touch-action: none;
  opacity: 0;

  ${TableStyled} th:hover & {
    opacity: 1;
  }

  ${(props) =>
    props.resizing &&
    css`
      background: ${Colors.GREEN3};
      opacity: 1;
    `}
`;

interface ICellWrapProps {
  depth?: number;
}
export const CellWrap = styled.div<ICellWrapProps>`
  ${(props) =>
    props.depth &&
    css`
      padding-left: ${props.depth * 2}rem;
    `}
  display: flex;
  align-items: center;
`;

export const TablePagination = styled.div`
  position: absolute;
  bottom: 0;
  z-index: 20;
  left: 0;
  right: 0;
  background-color: ${Colors.WHITE};
  height: ${sizes.paginationHeight}px;
  line-height: ${sizes.paginationHeight}px;
  border-top: solid 1px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  padding: 0 ${sizes.appPadding.small}px;

  > * {
    &:not(:first-child) {
      margin-left: ${sizes.appPadding.small}px;
    }
  }
`;

export const PaginationSelect = styled(HTMLSelect)`
  height: 24px;
  line-height: 24px;
  display: flex;
  align-items: center;

  > select {
    height: 24px;
  }

  > .bp4-icon {
    top: auto;
  }
`;
